import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import { FileQuestion, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HomeLink, StatusPage } from "@/components/states";
import { Button } from "@/components/ui/button";
import { setupStatusQuery } from "@/lib/queries";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // While the instance has no users every page leads to /setup; afterwards /setup is gone.
  beforeLoad: async ({ context, location }) => {
    const { setupRequired } = await context.queryClient.ensureQueryData(setupStatusQuery);
    const onSetup = location.pathname === "/setup";
    if (setupRequired && !onSetup) throw redirect({ to: "/setup" });
    if (!setupRequired && onSetup) throw redirect({ to: "/login" });
  },
  component: Outlet,
  notFoundComponent: NotFound,
  errorComponent: ErrorPage,
});

function NotFound() {
  const { t } = useTranslation();
  return (
    <StatusPage
      icon={FileQuestion}
      title={t("notFound.title")}
      description={t("notFound.description")}
      action={<HomeLink />}
    />
  );
}

function ErrorPage({ reset }: { reset: () => void }) {
  const { t } = useTranslation();
  return (
    <StatusPage
      icon={TriangleAlert}
      title={t("errorPage.title")}
      description={t("errorPage.description")}
      action={
        <Button
          variant="outline"
          onClick={() => {
            reset();
            window.location.reload();
          }}
        >
          {t("actions.retry")}
        </Button>
      }
    />
  );
}
